import axios from 'axios';
import { useAuthStore } from './stores/auth';
import { API_URL } from './config';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1800000, // 30 minute timeout — large bulk operations (e.g. 300k+ row customer uploads) can run long
});

// Request interceptor to add the token to headers
apiClient.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  const token = authStore.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.config?.url, error.response?.data);
    
    // Handle network errors
    if (!error.response) {
      console.error('Network Error:', error.message);
      error.message = 'Network error. Please check if the server is running.';
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      const authStore = useAuthStore();
      authStore.logout();
    }
    
    return Promise.reject(error);
  }
);

// --- TypeScript Interfaces --- //
interface Credentials {
  email?: string;
  password?: string;
}

export interface UserData {
  fullName?: string;
  email?: string;
  role?: 'Super Admin' | 'Admin' | 'Manager' | 'Agent' | 'User' | 'cmoUser';
  status?: 'active' | 'inactive';
  designation?: string;
  division?: string;
  password?: string;
  cmoCanEdit?: boolean;
}

interface ProfileData {
  fullName?: string;
  email?: string;
  designation?: string;
  division?: string;
}

// --- Authentication --- //
export const login = (credentials: Credentials) => {
  return apiClient.post('/auth/login', credentials);
};

export const register = (userData: UserData) => {
  return apiClient.post('/auth/register', userData);
};

// --- User Profile --- //
export const getProfile = () => {
  return apiClient.get('/users/profile');
};

export const getMe = () => {
  return apiClient.get('/users/me');
};

export const updateMe = (profileData: ProfileData) => {
  return apiClient.put('/users/me', profileData);
};

// --- Admin: User Management --- //
export const getUsers = () => {
  return apiClient.get('/users');
};

export const getUserById = (id: number | string) => {
  return apiClient.get(`/users/${id}`);
};

export const updateUser = (id: number | string, userData: UserData) => {
  return apiClient.put(`/users/${id}`, userData);
};

export const deleteUser = (id: number | string) => {
  return apiClient.delete(`/users/${id}`);
};

export const createUser = (userData: UserData) => {
  return apiClient.post('/users', userData);
};

// --- Customer --- //
export const getCustomerDetails = (meterNo: string) => {
  return apiClient.get('/customers/search', {
    params: { METER_NO: meterNo }
  });
};

// --- Content --- //
export const deleteContent = (id: number | string) => {
  return apiClient.delete(`/content/${id}`);
};

export default apiClient;

// --- Billing Profile / Meter Readings --- //
export const uploadBillingProfileFiles = (formData: FormData) => {
  return apiClient.post('/meter-readings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getMeterReadingsByMeterNo = (meterNo: string) => {
  return apiClient.get(`/meter-readings/${encodeURIComponent(meterNo)}`);
};

export const getMeterReadingsInBulk = (meterNos: string[]) => {
  return apiClient.post('/meter-readings/bulk', { meterNos });
};

export const deleteAllBillingProfileData = () => {
  return apiClient.delete('/meter-readings/all');
};

// Estimation endpoints
export const calculateEstimates = (meterNo: string) => {
  return apiClient.post(`/meter-readings/estimate/${encodeURIComponent(meterNo)}`);
};

export const saveEstimates = (meterNo: string, estimates: any[]) => {
  return apiClient.post('/meter-readings/save-estimates', { meter_no: meterNo, estimates });
};

export const getCoveredMonths = (meterNo: string) => {
  return apiClient.get(`/meter-readings/covered-months/${encodeURIComponent(meterNo)}`);
};

// --- Complaints --- //
export const getComplaints = (params?: { category?: string; status?: string; search?: string; page?: number; limit?: number }) => {
  return apiClient.get('/complaints', { params });
};

export const getComplaintById = (id: number | string) => {
  return apiClient.get(`/complaints/${id}`);
};

export const createComplaint = (complaintData: {
  complaintCategoryId: number;
  customerId: string;
  issueDescription: string;
  status?: string
}) => {
  return apiClient.post('/complaints', complaintData);
};

export const updateComplaint = (id: number | string, complaintData: any) => {
  return apiClient.put(`/complaints/${id}`, complaintData);
};

export const getComplaintAiSuggestion = (id: number | string) => {
  return apiClient.post(`/complaints/${id}/ai-suggest`, {});
};

export const deleteComplaint = (id: number | string) => {
  return apiClient.delete(`/complaints/${id}`);
};

export const getCustomerInfo = (customerId: string) => {
  return apiClient.get(`/complaints/customer/${customerId}`);
};

export const getComplaintAnalytics = (params?: { startDate?: string; endDate?: string; agentId?: string }) => {
  return apiClient.get('/complaints/analytics', { params });
};

export const getComplaintAgents = () =>
  apiClient.get('/complaints/agents');

export const getComplaintComments = (complaintId: number | string) => {
  return apiClient.get(`/complaints/${complaintId}/comments`);
};

export const addComplaintComment = (complaintId: number | string, content: string) => {
  return apiClient.post(`/complaints/${complaintId}/comments`, { content });
};

export const deleteComplaintComment = (complaintId: number | string, commentId: number | string) => {
  return apiClient.delete(`/complaints/${complaintId}/comments/${commentId}`);
};

export const getBillStopAnalysis = (customerId: string, meterNo: string) => {
  return apiClient.get('/bill-stop/analyze', {
    params: {
      customerNum: customerId,
      meterNo: meterNo
    }
  });
};

export const getMeterReplacementHistory = (meterNo: string) => {
  return apiClient.get(`/meter-replacements/history/${encodeURIComponent(meterNo)}`);
};

export const getCustomerBillingInfo = (searchValue: string) => {
  return apiClient.get(`/complaints/billing-info/${encodeURIComponent(searchValue)}`);
};

// --- Complaint Categories --- //
export const getComplaintCategories = () => {
  return apiClient.get('/complaint-categories');
};

export const createComplaintCategory = (categoryData: { name: string }) => {
  return apiClient.post('/complaint-categories', categoryData);
};

export const updateComplaintCategory = (id: number | string, categoryData: { name: string }) => {
  return apiClient.put(`/complaint-categories/${id}`, categoryData);
};

export const deleteComplaintCategory = (id: number | string) => {
  return apiClient.delete(`/complaint-categories/${id}`);
};

// --- CMO (Change Meter Owner) --- //
export const getCMOs = (params?: { page?: number; limit?: number; status?: string; search?: string; sortBy?: string; sortOrder?: string; nocs?: string; dateFrom?: string; dateTo?: string; isMDMEntry?: string; cpcCpr?: string }) => {
  return apiClient.get('/cmo', { params });
};

export const getCMOFilterOptions = () => {
  return apiClient.get('/cmo/filter-options');
};

export const getCMOMultiSearch = (ids: string) => {
  return apiClient.get('/cmo/multi-search', { params: { ids } });
};

export const getCMOStatistics = () => {
  return apiClient.get('/cmo/statistics');
};

export const getCMOMDMStatistics = () => {
  return apiClient.get('/cmo/mdm-statistics');
};

export const getCMOMigrationStats = (params?: { nocs?: string; cpcCpr?: string; apiStartPage?: number; dateFrom?: string; dateTo?: string }) => {
  return apiClient.get('/cmo/migration-stats', { params });
};

export const getCMODateMigrationStats = (dateFrom: string, dateTo: string) => {
  return apiClient.get('/cmo/date-migration-stats', { params: { dateFrom, dateTo } });
};

export const checkMDMEntry = () => {
  return apiClient.post('/cmo/check-mdm-entry');
};

export const getCMOExportData = (params?: { search?: string; isApproved?: string; nocs?: string; dateFrom?: string; dateTo?: string; isMDMEntry?: string; cpcCpr?: string }) => {
  return apiClient.get('/cmo/export', { params });
};

// Same as getCMOExportData but with no 50k row cap — used by Details Report (Report + Advanced sub-tabs)
// packageRanges: JSON-stringified PackageRangeInput[] — narrows the query server-side to meters
// falling inside the selected package's Company/Phase ranges (optional).
export const getCMOFullExportData = (params?: { search?: string; isApproved?: string; nocs?: string; dateFrom?: string; dateTo?: string; isMDMEntry?: string; cpcCpr?: string; packageRanges?: string }) => {
  return apiClient.get('/cmo/export-full', { params });
};

export const uploadCustomerInfo = (data: any[]) => {
  return apiClient.post('/cmo/upload-customers', { customers: data });
};

// Packages: meter-number-range groupings (L&G/Wasion/Shenzhen Star x 1P/3P), stored in cms's own SQLite DB
export interface PackageRangeInput {
  company: 'L&G' | 'Wasion' | 'Shenzhen Star';
  phase: '1P' | '3P';
  rangeStart: string;
  rangeEnd: string;
}
export const getPackages = () => apiClient.get('/packages');
export const createPackage = (name: string, ranges: PackageRangeInput[]) => apiClient.post('/packages', { name, ranges });
export const updatePackage = (id: number, name: string, ranges: PackageRangeInput[]) => apiClient.put(`/packages/${id}`, { name, ranges });
export const deletePackage = (id: number) => apiClient.delete(`/packages/${id}`);

export const getCMOCustomerFull = (consumerId: string) => {
  return apiClient.get(`/cmo/customer-full/${encodeURIComponent(consumerId)}`);
};

export const checkCMOMeterNo = (meterNo: string, excludeId: number) => {
  return apiClient.get('/cmo/check-meter', { params: { meterNo, excludeId } });
};

export const updateCMO = (id: number, data: Record<string, any>) => {
  return apiClient.patch(`/cmo/${id}`, data);
};

export const updateCMOCustomer = (oldConsumerId: string, data: Record<string, any>) => {
  return apiClient.patch(`/cmo/customer/${oldConsumerId}`, data);
};

// id (the record's own primary key) targets exactly one row -- the only way
// to remove one specific record among duplicates sharing the same
// consumer+meter. Falls back to consumerId(+meterNo) when not given.
export const deleteCMO = (consumerId: string, meterNo?: string, id?: number | string) => {
  const params: Record<string, any> = {};
  if (id !== undefined && id !== '') params.id = id;
  else if (meterNo) params.meterNo = meterNo;
  return apiClient.delete(`/cmo/${encodeURIComponent(consumerId)}`, { params: Object.keys(params).length > 0 ? params : undefined });
};

export const getCMOCustomerInfo = (consumerId: string) => {
  return apiClient.get('/cmo/customer-info', { params: { consumerId } });
};

// --- Bill Stop Snapshot (OTBL_CMS SQL Server) --- //
export const getBillStopSnapshot = (params?: {
  page?: number;
  limit?: number;
  coverage?: string;
  tariff?: string;
  search?: string;
  hasRead?: string;
  installed?: string;
}) => {
  return apiClient.get('/bill-stop/snapshot', { params });
};

export const getBillStopSnapshotSummary = () => {
  return apiClient.get('/bill-stop/snapshot/summary');
};

// --- CMO Meter Replacement --- //
export const getCMOMeterReplacements = (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
  return apiClient.get('/cmo-meter-replacement', { params });
};

export const getCMOMeterReplacementStats = () => {
  return apiClient.get('/cmo-meter-replacement/stats');
};

export const processCMOMeterReplacement = (id: number, data: { OldConsumerId: string; OldMeterNumber: string; ReplaceMeterNumber: string; ReplaceDate: string; CustomerName?: string }) => {
  return apiClient.post(`/cmo-meter-replacement/${id}/process`, data);
};

export const processAllOpenCMOMeterReplacements = () => {
  return apiClient.post('/cmo-meter-replacement/process-all-open');
};

export const autoResolveCMOMeterReplacements = () => {
  return apiClient.post('/cmo-meter-replacement/auto-resolve');
};

export const exportInProcessMeterReplacements = () => {
  return apiClient.get('/cmo-meter-replacement/export-inprocess');
};

export const getInProcessEnrichedMeterReplacements = () => {
  return apiClient.get('/cmo-meter-replacement/inprocess-enriched');
};

export const updateCMOMeterReplacementStatus = (id: number, status: string) => {
  return apiClient.patch(`/cmo-meter-replacement/${id}/status`, { status });
};

export const updateCMOMeterReplacementRecord = (id: number, data: Record<string, any>) => {
  return apiClient.put(`/cmo-meter-replacement/${id}`, data);
};

export const getExcludedMeterReplacements = () => {
  return apiClient.get('/cmo-meter-replacement-exclusions');
};

export const addMeterReplacementExclusion = (cmoRecordId: number, oldConsumerId: string) => {
  return apiClient.post('/cmo-meter-replacement-exclusions', { cmoRecordId, oldConsumerId });
};

export const removeMeterReplacementExclusion = (cmoRecordId: number) => {
  return apiClient.delete(`/cmo-meter-replacement-exclusions/${cmoRecordId}`);
};

// --- CMO Meter Swap --- //
export const getCMOMeterSwaps = (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
  return apiClient.get('/cmo-meter-swap', { params });
};

export const getCMOMeterSwapStats = () => {
  return apiClient.get('/cmo-meter-swap/stats');
};

export const updateCMOMeterSwapStatus = (id: number, status: string) => {
  return apiClient.patch(`/cmo-meter-swap/${id}/status`, { status });
};

export const autoResolveCMOMeterSwaps = () => {
  return apiClient.post('/cmo-meter-swap/auto-resolve');
};

export const markAllCMOMeterSwapsInProcess = () => {
  return apiClient.post('/cmo-meter-swap/mark-all-inprocess');
};

export const getCMOMeterSwapPairLabels = () => {
  return apiClient.get('/cmo-meter-swap/pair-labels');
};

export const getCMOMeterSwapPairSaIds = () => {
  return apiClient.get('/cmo-meter-swap/pair-sa-ids');
};

export const syncCMOMeterSwapPairTypes = () => apiClient.post('/cmo-meter-swap/sync-pair-swaps');
export const getCMOMeterSwapTypes = () => apiClient.get('/cmo-meter-swap/swap-types');
export const getCMOMeterSwapCycleIds = () => apiClient.get('/cmo-meter-swap/cycle-ids');
export const updateCMOMeterSwapType = (id: number, swapType: string) => apiClient.patch(`/cmo-meter-swap/${id}/swap-type`, { swap_type: swapType });
export const clearCMOMeterSwapType = (id: number) => apiClient.delete(`/cmo-meter-swap/${id}/swap-type`);
export const getCMOMeterSwapTypeOptions = () => apiClient.get('/cmo-meter-swap/type-options');
export const addCMOMeterSwapTypeOption = (label: string) => apiClient.post('/cmo-meter-swap/type-options', { label });
export const deleteCMOMeterSwapTypeOption = (id: number) => apiClient.delete(`/cmo-meter-swap/type-options/${id}`);
export const syncCMOMeterSwapRecords = (records: Record<string, any>[]) => apiClient.post('/cmo-meter-swap/sync', { records });
export const getCMOSecurityUsers = () => apiClient.get('/cmo-meter-swap/security-users');

// --- Roaster Schedule --- //
export const getRoasterSchedule = () => {
  return apiClient.get('/roaster');
};

export const getCMOCustomerSearch = (q: string) => {
  return apiClient.get('/cmo/customer-search', { params: { q } });
};

export const saveRoasterSchedule = (
  entries: { date: string; day: string; name: string; email?: string; assignment?: string | null }[],
  meta?: { action: 'edit' | 'upload'; changes?: { field: string; before: string; after: string }[] }
) => {
  return apiClient.post('/roaster', { entries, ...meta });
};

export const getRoasterHistory = () => {
  return apiClient.get('/roaster/history');
};